/**
 * ImpersonationBanner — permanent, unmissable banner shown on EVERY page
 * while a support session is impersonating a user (LAU F2b,
 * docs/login-as-user-design.md ADDENDUM V2 section 5).
 *
 * Data source: useAuth() -> /me -> session.impersonation. Zero new fetch;
 * the field is null on normal sessions so the banner renders nothing then.
 *
 * Authorization note: this component is DISPLAY-ONLY. The "Exit" button is
 * a trigger, not an authorization boundary -- POST /api/auth/impersonation/
 * exit derives the session to revoke exclusively from the signed cookie
 * (user.sessionId), never from client input. The banner cannot exit a
 * session that is not the caller's own.
 *
 * Colour: amber/red house tokens, deliberately loud so there is never a
 * second of confusion about whose account is on screen (design section 5).
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogOut, ShieldAlert } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth, meQueryKey } from "@/lib/auth/context";

/** Format seconds-remaining as m:ss (or "0:00" once elapsed). */
function formatRemaining(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const { impersonation } = useAuth();
  const queryClient = useQueryClient();

  const expiresAt = impersonation?.expires_at ?? null;

  // Live countdown. Tick every second only while a banner is active.
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!impersonation) return;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [impersonation]);

  const exitMutation = useMutation({
    mutationFn: () => api.impersonationExit(),
    onSettled: async () => {
      // Refetch /me: the impersonation cookie is now cleared, so the next
      // /me runs on the admin's own session and the banner disappears.
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      // Hard reload to a safe landing so no impersonated-context UI lingers.
      window.location.assign("/");
    },
  });

  if (!impersonation) return null;

  const remaining = expiresAt !== null ? expiresAt - now : null;
  const timeLabel = remaining !== null ? formatRemaining(remaining) : null;
  const expired = remaining !== null && remaining <= 0;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b border-amber-500/50 bg-amber-500 px-4 py-2.5 text-center text-[13px] font-semibold text-amber-950 shadow-sm animate-fade-in sm:px-6"
    >
      <ShieldAlert className="h-4 w-4 shrink-0 stroke-[2]" aria-hidden="true" />
      <span className="hidden sm:inline">
        {t("impersonation.banner.label")}</span>
      <span className="sm:hidden">{t("impersonation.banner.label_short")}</span>
      {timeLabel !== null ? (
        <span
          className="rounded-full bg-amber-950/10 px-2 py-0.5 font-mono tabular-nums"
          title={t("impersonation.banner.time_tooltip")}
        >
          {expired ? t("impersonation.banner.expiring") : timeLabel}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => exitMutation.mutate()}
        disabled={exitMutation.isPending}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-950 px-3 py-1 text-[12px] font-semibold text-amber-50 transition-colors hover:bg-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-950/40 disabled:opacity-60"
      >
        <LogOut className="h-3.5 w-3.5 stroke-[2]" aria-hidden="true" />
        <span>{t("impersonation.banner.exit")}</span>
      </button>
    </div>
  );
}
